import React, { useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import styles from '../style.module.css';

/**
 * SearchInput Component
 * A reusable search input with icon and clear functionality
 * 
 * @param {Object} props - Component props
 * @param {string} props.value - Current value of the input
 * @param {Function} props.onChange - Callback when input value changes
 * @param {Function} [props.onFocus] - Callback when input is focused
 * @param {Function} [props.onBlur] - Callback when input loses focus
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.inputProps] - Additional props to pass to the input element
 * @returns {JSX.Element} Search input component
 */
const SearchInput = ({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder = 'Search for businesses...',
  className = '',
  inputProps = {},
}) => {
  const inputRef = useRef(null);

  // Focus the input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Focus search input when '/' is pressed
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleClear = () => {
    if (onChange) {
      onChange({ target: { value: '' } });
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={classNames(styles.searchContainer, className)}>
      <span className="p-input-icon-left w-full">
        <i className="pi pi-search" />
        <InputText
          ref={inputRef}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          className="w-full"
          aria-label="Search for businesses"
          {...inputProps}
        />
        {value && (
          <button
            type="button"
            className="p-input-icon-right"
            onClick={handleClear}
            aria-label="Clear search"
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-color-secondary)',
            }}
          >
            <i className="pi pi-times" />
          </button>
        )}
        <div 
          className={classNames(styles.shortcutHint, { 'hidden': document.activeElement === inputRef.current })}
          style={{
            position: 'absolute',
            right: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '0.75rem',
            color: 'var(--text-color-secondary)',
            backgroundColor: 'var(--surface-ground)',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            border: '1px solid var(--surface-border)',
            pointerEvents: 'none',
            opacity: 0.8,
          }}
        >
          /
        </div>
      </span>
    </div>
  );
};

export default SearchInput;
